/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import styles from '../../../../styles/components/pageComponents/program/ProgramContentBuilder.module.css';
import { ActivityType } from '../../../../types/content';
import { NewActivityTypeModal } from '../activity/NewActivityTypeModal';
import { useActivityTypes } from '../../../../hooks/useActivityType';
import ActivityDropdown from '../../../controlVariations/ActivityDropdown';
import { TimePicker } from '../../../common/TimePicker';

interface ActivityItemProps {
	activity: ActivityType;
	index: number;
	onUpdate: (
		id: string,
		field: keyof ActivityType,
		value: any,
		errors?: ActivityType['errors']
	) => void;
	onDelete: (id: string) => void;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
	activity,
	index,
	onUpdate,
	onDelete,
}) => {
	const [isExpanded, setIsExpanded] = useState(true);

	const { activityTypes, fetchActivityTypes } = useActivityTypes();

	const [isModalOpen, setIsModalOpen] = useState(false);

	const [timeErrors, setTimeErrors] = useState({
		startTime: '',
		endTime: '',
	});

	const dropdownOptions = activityTypes?.map(type => ({
		label: type.title,
		value: type.id, // or type.name depending on backend
		color: type.color || '#ccc',
	}));

	const selectedOption = dropdownOptions?.find(
		opt => opt.value === activity.type
	);

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
		>,
		field: keyof ActivityType
	) => {
		onUpdate(activity.id, field, e.target.value);
	};

	const isEndTimeValid = (
		startTime: string,
		endTime: string
	) => {
		if (!startTime || !endTime) return true;

		const [sh, sm] = startTime.split(':').map(Number);
		const [eh, em] = endTime.split(':').map(Number);

		if (eh < sh) return false;

		if (eh === sh && em < sm) return false;

		return true;
	};

	return (
		<div className={styles.activityCard}>
			<div className={styles.cardHeader}>
				<h4 className={styles.cardTitle}>
					Activity {index + 1}{' '}
					{activity.name && (
						<span className={styles.cardTitleGreen}>
							{' '}
							{activity.name}
						</span>
					)}
				</h4>
				<div className={styles.cardControls}>
					<button
						className={styles.iconBtn}
						onClick={() => onDelete(activity.id)}
						title="Delete Activity"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="22"
							viewBox="0 0 20 22"
							fill="none"
						>
							<path
								d="M7.75 9.75V15.75M11.75 9.75V15.75M16.75 4.75V18.75C16.75 19.2804 16.5393 19.7891 16.1642 20.1642C15.7891 20.5393 15.2804 20.75 14.75 20.75H4.75C4.21957 20.75 3.71086 20.5393 3.33579 20.1642C2.96071 19.7891 2.75 19.2804 2.75 18.75V4.75M0.75 4.75H18.75M5.75 4.75V2.75C5.75 2.21957 5.96071 1.71086 6.33579 1.33579C6.71086 0.960714 7.21957 0.75 7.75 0.75H11.75C12.2804 0.75 12.7891 0.960714 13.1642 1.33579C13.5393 1.71086 13.75 2.21957 13.75 2.75V4.75"
								stroke="#1E1E1E"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</button>
					<button
						className={styles.iconBtn}
						onClick={() => setIsExpanded(!isExpanded)}
					>
						{isExpanded ? (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="8"
								viewBox="0 0 14 8"
								fill="none"
							>
								<path
									d="M13 7L7 1L1 7"
									stroke="#949494"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						) : (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="8"
								viewBox="0 0 14 8"
								fill="none"
							>
								<path
									d="M1 1L7 7L13 1"
									stroke="#949494"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						)}
					</button>
				</div>
			</div>

			{isExpanded && (
				<div className={styles.cardBody}>
					<div className={styles.formRow}>
						<div className={styles.formGroup} style={{ flex: 1.5 }}>
							<label className={styles.label}>
								Activity Name
								<span className={styles.required}>*</span>
							</label>
							<input
								type="text"
								className={styles.inputControl}
								placeholder={`Masterclass ${index + 1}`}
								value={activity.name}
								onChange={e => handleChange(e, 'name')}
							/>
							{activity.errors?.name && (
								<span className={styles.error}>
									{activity.errors.name}
								</span>
							)}
						</div>

						<div className={styles.formGroup} style={{ flex: 1 }}>
							<div className={styles.labelWrapper}>
								<label className={styles.label}>
									Activity Type
									<span className={styles.required}>*</span>
								</label>
								<span
									className={styles.linkText}
									onClick={() => setIsModalOpen(true)}
									style={{ cursor: 'pointer' }}
								>
									New Activity Type +
								</span>

								{activity.errors?.type && (
									<span className={styles.error}>
										{activity.errors.type}
									</span>
								)}
							</div>

							<div style={{ width: '100%' }}>
								<ActivityDropdown
									placeholder="Select Activity Type"
									options={dropdownOptions || []}
									selected={selectedOption?.label}
									onSelect={option => {
										onUpdate(
											activity.id,
											'type',
											option.value
										);
									}}
									height="40px"
								/>
							</div>
						</div>

						<div className={styles.formGroup}>
							<label className={styles.label}>Date</label>
							<div className={styles.inputGroup}>
								<span className={styles.iconWrapper}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="20"
										height="22"
										viewBox="0 0 20 22"
										fill="none"
									>
										<path
											d="M5.75 0.75V4.75M13.75 0.75V4.75M0.75 8.75H18.75M2.75 2.75H16.75C17.8546 2.75 18.75 3.64543 18.75 4.75V18.75C18.75 19.8546 17.8546 20.75 16.75 20.75H2.75C1.64543 20.75 0.75 19.8546 0.75 18.75V4.75C0.75 3.64543 1.64543 2.75 2.75 2.75Z"
											stroke="#1E1E1E"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</span>
								<input
									type="date"
									className={styles.inputControl}
									value={activity.date}
									min={new Date().toISOString().split('T')[0]} // 🚫 restrict past dates
									onChange={e => {
										handleChange(e, 'date');

										if (!e.target.value) {
											setTimeErrors({
												startTime: '',
												endTime: '',
											});
										}
									}}
								/>
							</div>
						</div>

						<div className={styles.formGroup}>
							<label className={styles.label}>Duration</label>
							<div className={styles.inputGroup}>
								<span className={styles.iconWrapper}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="22"
										height="22"
										viewBox="0 0 22 22"
										fill="none"
									>
										<path
											d="M10.75 4.75V10.75L14.75 12.75M20.75 10.75C20.75 16.2728 16.2728 20.75 10.75 20.75C5.22715 20.75 0.75 16.2728 0.75 10.75C0.75 5.22715 5.22715 0.75 10.75 0.75C16.2728 0.75 20.75 5.22715 20.75 10.75Z"
											stroke="#1E1E1E"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</span>
								<TimePicker
									value={activity.startTime || ''}
									// maxTime={activity.endTime}
									onChange={val => {

										const errors = {
											...activity.errors,
											endTime: undefined,
										};
										onUpdate(activity.id, 'startTime', val, errors);

										// If the current end time becomes invalid,
										// clear it automatically.
										if (
											activity.endTime &&
											!isEndTimeValid(val, activity.endTime)
										) {
											const errors = {
												...activity.errors,
												endTime: 'End time must be greater than or equal to start time.',
											};

											onUpdate(activity.id, 'startTime', val, errors);

											setTimeErrors(prev => ({
												...prev,
												endTime:
													'End time must be greater than or equal to start time.',
											}));

											return;
										}

										setTimeErrors(prev => ({
											...prev,
											startTime: '',
											endTime: '',
										}));
									}}
								/>

								<span className={styles.separator}>to</span>

								<TimePicker
									value={activity.endTime || ''}
									minTime={activity.startTime}
									hasError={!!timeErrors.endTime}
									onChange={val => {
										onUpdate(activity.id, 'endTime', val);

										setTimeErrors(prev => ({
											...prev,
											endTime: '',
										}));
									}}
								/>

								{/* {timeErrors.endTime && (
	<div className={styles.error}>
		{timeErrors.endTime}
	</div>
)} */}
							</div>
							{activity.date && (
								<>
									{!activity.startTime && !activity.endTime && (
										<div className={styles.error}>
											Start and End times are required when a date is selected
										</div>
									)}
								</>
							)}
						</div>


					</div>

					<div className={styles.formGroup}>
						<label className={styles.label}>Description</label>
						<textarea
							className={`${styles.inputControl} ${styles.textarea}`}
							placeholder="Enter details (Optional)"
							maxLength={500}
							value={activity.description}
							onChange={e => handleChange(e, 'description')}
						/>
						<span className={styles.charCount}>
							{activity.description.length}/500
						</span>
					</div>
				</div>
			)}

			<NewActivityTypeModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				onSuccess={async () => {
					await fetchActivityTypes(); // 🔥 refresh dropdown data
					setIsModalOpen(false);
				}}
			/>
		</div>
	);
};
